import { Injectable } from '@angular/core';
import { deleteArchiveDataModalData } from '@app/mpp/model/delete-archive-model';
import {
  cardClickEvent,
  mppRecipeCard,
} from '@app/mpp/model/recipe-card-model';
import { ActionSheetController, PopoverController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { EllipsisOptionsPopoverComponent } from '@shared/components/popovers/ellipsis-options/ellipsis-options-popover.component';
import {
  ANALYTICS_ACTIONS,
  baseMPPTransform,
  getAllRecipes,
  MPP_ACTIONS,
  MPPAnalyticsConstants,
  MPPTracingConstants,
  RECIPE_ACTIONS,
  RecipeSortOptions,
  SortDirections,
  actionTypeTransformer,
} from '@usf/ngrx-tools';
import { createMppData } from '@app/mpp/model/create-mpp-model';
import { copyMppModalData } from '@app/mpp/model/copy-mpp-model';
import { Recipe, SourceSystem, State as MppState, User } from '@usf/mpp-types';
import { SortableSectionHeader } from '@shared/components/sortable-section/models/sortable-section.model';
import { RecipeModalSortOptions } from '@app/mpp/model/modal-values';
import { CopyRecipeCreateModalComponent } from '../../../shared/components/modal/copy-recipe-create-modal/copy-recipe-create-modal.component';
import { ArchiveRecipe, CopyRecipe } from '@usf/ngrx-tools/lib/model/recipe';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class RecipeService {
  // used to show/hide the delet confirmation modal
  deleteModalIsOpen = false;
  // flip to true when requesting an action by selection
  actionBySelection = false;
  deleteArchiveData: deleteArchiveDataModalData;
  copyMppData: copyMppModalData;
  constructor(
    readonly store: Store,
    private popoverController: PopoverController,
    private actionSheetController: ActionSheetController,
    private translateService: TranslateService,
  ) {}

  selectRecipe(recipeId: string) {
    // will flip selection value back and forth for recipe.
    this.store.dispatch(RECIPE_ACTIONS.selectRecipe({ recipeId }));
  }

  openDeleteRecipesModal(
    recipeIds: string[],
    recipesToFilter: mppRecipeCard[],
  ) {
    const recipes: mppRecipeCard[] = recipesToFilter.filter(data =>
      recipeIds.includes(data.recipeId),
    );

    const dependentRecipes: string = recipes.reduce((tracking, data) => {
      if (!!data.referencedRecipeList && data.referencedRecipeList.length > 0) {
        if (tracking.length < 0)
          return tracking + ', ' + data.referencedRecipeList.join(', ');
        return data.referencedRecipeList.join(', ');
      } else {
        return tracking;
      }
    }, '');
    const dependentMenuItems: string = recipes.reduce((tracking, data) => {
      if (
        !!data.referencedMenuItemList &&
        data.referencedMenuItemList.length > 0
      ) {
        if (tracking.length < 0)
          return tracking + ', ' + data.referencedMenuItemList.join(', ');
        return data.referencedMenuItemList.join(', ');
      } else {
        return tracking;
      }
    }, '');
    const dependentMenus: string = recipes.reduce((tracking, data) => {
      if (
        !!data.referencedEventPlanList &&
        data.referencedEventPlanList.length > 0
      ) {
        if (tracking.length < 0)
          return tracking + ', ' + data.referencedEventPlanList.join(', ');
        return data.referencedEventPlanList.join(', ');
      } else {
        return tracking;
      }
    }, '');
    this.store.dispatch(
      ANALYTICS_ACTIONS.deleteRecipe({
        tracking: {
          analytics: {
            data: {
              actionType: MPPAnalyticsConstants.deleteRecipe,
              event: MPPAnalyticsConstants.recipeActionEvent,
              capability: MPPAnalyticsConstants.capability,
            },
            transformFunc: actionTypeTransformer,
          },
        },
      }),
    );
    this.deleteArchiveData = {
      selectedIds: recipeIds,
      dependentRecipes,
      dependentMenuItems,
      dependentMenus,
      recipes,
    };
    this.deleteModalIsOpen = true;
  }

  openCopyRecipesModal = (
    recipeCopy: Recipe,
    recipes: mppRecipeCard[],
    copyRecipeModal: CopyRecipeCreateModalComponent,
  ) => {
    this.copyMppData = {
      recipes,
      recipeCopy,
    };
    this.store.dispatch(
      ANALYTICS_ACTIONS.copyRecipe({
        tracking: {
          analytics: {
            data: {
              actionType: MPPAnalyticsConstants.copyRecipe,
              event: MPPAnalyticsConstants.recipeActionEvent,
              capability: MPPAnalyticsConstants.capability,
            },
            transformFunc: actionTypeTransformer,
          },
        },
      }),
    );
    // needs a frame for the data to register

    setTimeout(() => {
      copyRecipeModal.openModal();
    }, 100);
  };

  copyRecipeSameCustomerAction(recipe: Recipe) {
    console.log('updated copy recipe', recipe);
    const requestDto: CopyRecipe = {
      recipes: [recipe],
      destinationCustomers: [
        {
          destinationCustomerNumber: recipe.customerNumber,
          destinationDepartmentNumber: recipe.departmentNumber,
          destinationDivisionNumber: recipe.divisionNumber,
          userId: JSON.stringify(recipe.createdUserId),
          user: {
            ...recipe.createdByUser,
          },
        },
      ],
      userId: recipe.createdByUser.userName,
    };
    console.log('RequestDto', requestDto);
    this.store.dispatch(
      MPP_ACTIONS.copyRecipes({
        tracking: {
          tracing: {
            data: {
              traceContext: MPPTracingConstants.copyRecipes,
              isEndOfTrace: false,
              isStartOfTrace: true,
              attributes: {
                event: MPPTracingConstants.copyRecipesEvent,
                spanName: MPPTracingConstants.copyRecipesSpan,
              },
            },
            transformFunc: baseMPPTransform,
          },
        },
      }),
    );
    this.store.dispatch(
      RECIPE_ACTIONS.copyRecipe({
        copyRecipes: requestDto,
      }),
    );
  }

  closeDeleteRecipesModal() {
    this.deleteModalIsOpen = false;
  }

  deselectSelections() {
    this.store.dispatch(RECIPE_ACTIONS.resetSelections());
  }

  archiveRecipesAction(archiveRecipe: ArchiveRecipe) {
    archiveRecipe.isArchive
      ? this.store.dispatch(
          ANALYTICS_ACTIONS.archiveRecipe({
            tracking: {
              analytics: {
                data: {
                  actionType: MPPAnalyticsConstants.archiveRecipe,
                  event: MPPAnalyticsConstants.recipeActionEvent,
                  capability: MPPAnalyticsConstants.capability,
                },
                transformFunc: actionTypeTransformer,
              },
            },
          }),
        )
      : this.store.dispatch(
          ANALYTICS_ACTIONS.unArchiveRecipe({
            tracking: {
              analytics: {
                data: {
                  actionType: MPPAnalyticsConstants.unArchiveRecipe,
                  event: MPPAnalyticsConstants.recipeActionEvent,
                  capability: MPPAnalyticsConstants.capability,
                },
                transformFunc: actionTypeTransformer,
              },
            },
          }),
        );
    this.store.dispatch(
      MPP_ACTIONS.archiveRecipes({
        tracking: {
          tracing: {
            data: {
              traceContext: MPPTracingConstants.archiveRecipes,
              isEndOfTrace: false,
              isStartOfTrace: true,
              attributes: {
                event: MPPTracingConstants.archiveRecipesEvent,
                spanName: MPPTracingConstants.archiveRecipesSpan,
              },
            },
            transformFunc: baseMPPTransform,
          },
        },
      }),
    );
    this.store.dispatch(RECIPE_ACTIONS.archiveRecipes({ archiveRecipe }));
    if (this.actionBySelection) this.deselectSelections();
  }

  deleteRecipesFromState(recipeIds: string[]) {
    this.store.dispatch(
      MPP_ACTIONS.deleteRecipes({
        tracking: {
          tracing: {
            data: {
              traceContext: MPPTracingConstants.deleteRecipes,
              isEndOfTrace: false,
              isStartOfTrace: true,
              attributes: {
                event: MPPTracingConstants.deleteRecipesEvent,
                spanName: MPPTracingConstants.deleteRecipesSpan,
              },
            },
            transformFunc: baseMPPTransform,
          },
        },
      }),
    );
    this.store.dispatch(RECIPE_ACTIONS.deleteRecipes({ recipeIds }));
    if (this.actionBySelection) this.deselectSelections();
  }

  getRecipeActionButtons(
    event: cardClickEvent,
    recipes: mppRecipeCard[],
    copyRecipeModalReference: CopyRecipeCreateModalComponent,
  ) {
    return [
      {
        text: this.translateService.instant('i18n.menuProfitPro.actions.copy'),
        handler: async () => {
          const recipesToGrabFrom = await firstValueFrom(
            this.store.select(getAllRecipes()),
          );
          const recipe = recipesToGrabFrom.find(
            item => item.recipeId === event.recipeId,
          );
          this.actionBySelection = false;
          this.openCopyRecipesModal(recipe, recipes, copyRecipeModalReference);
          this.popoverController.dismiss();
        },
      },
      {
        text: event.archiveInd
          ? this.translateService.instant(
              'i18n.menuProfitPro.actions.unarchive',
            )
          : this.translateService.instant('i18n.menuProfitPro.actions.archive'),
        handler: () => {
          this.actionBySelection = false;
          const archiveRecipe: ArchiveRecipe = {
            recipeIds: [event.recipeId],
            isArchive: !event.archiveInd, // Invert the current archive state
          };
          this.archiveRecipesAction(archiveRecipe);
          this.popoverController.dismiss();
        },
      },
      {
        text: this.translateService.instant(
          'i18n.menuProfitPro.actions.delete',
        ),
        handler: () => {
          this.actionBySelection = false;
          this.openDeleteRecipesModal([event.recipeId], recipes);
          this.popoverController.dismiss();
        },
      },
    ];
  }

  async showEllipsisOptionsTouch(buttons: any[]) {
    buttons.push({
      text: this.translateService.instant('i18n.common.cancel'),
      role: 'cancel',
      cssClass: 'cancel-button',
    });
    const actionSheet = await this.actionSheetController.create({
      cssClass: 'action-sheet-touch',
      mode: 'ios',
      buttons,
    });
    await actionSheet.present();
  }

  async showEllipsisOptionsDesktop(buttons: any[]) {
    const popover = await this.popoverController.create({
      component: EllipsisOptionsPopoverComponent,
      componentProps: {
        buttons,
      },
      showBackdrop: false,
      cssClass: 'ellipsis-options-popover-desktop',
      event,
      mode: 'ios',
      alignment: 'center',
    });
    await popover.present();
  }

  createRecipe(createMppData: createMppData) {
    let recipe: Recipe = {
      recipeName: createMppData.name,
      description: createMppData.description,
      lockInd: createMppData.lockIndicator,
      recipeId: '',
      divisionNumber: 0,
      customerNumber: 0,
      departmentNumber: 0,
      state: createMppData.publicPermission
        ? MppState.PUBLIC
        : MppState.INTERNAL,
      createdUserId: '',
      createdDtm: '',
      updateUserId: '',
      updateDtm: '',
      servingPerBatch: 1,
      costPerServing: 0.0,
      cost: 0.0,
      sourceSystem: SourceSystem.ECOMR4,
      updatedByUser: {
        firstName: '',
        lastName: '',
        userName: '',
      },
      createdByUser: {
        firstName: '',
        lastName: '',
        userName: '',
      },
    };
    this.store.dispatch(
      MPP_ACTIONS.createRecipe({
        tracking: {
          tracing: {
            data: {
              traceContext: MPPTracingConstants.createRecipe,
              isEndOfTrace: false,
              isStartOfTrace: true,
              attributes: {
                event: MPPTracingConstants.createRecipeEvent,
                spanName: MPPTracingConstants.createRecipeSpan,
              },
            },
            transformFunc: baseMPPTransform,
          },
        },
      }),
    );
    this.store.dispatch(RECIPE_ACTIONS.createRecipe({ recipe }));
  }
  selectHeader(sortableSectionHeader: SortableSectionHeader) {
    this.store.dispatch(
      RECIPE_ACTIONS.updateRecipesSort({
        selectedHeader: sortableSectionHeader.headerName,
        sortDirection: sortableSectionHeader.sortDirection,
      }),
    );
  }

  directlyUpdateSort(selection: RecipeModalSortOptions) {
    let selectedHeader = '';
    let sortDirection = '';
    switch (selection) {
      case RecipeModalSortOptions.RecipeAZ:
        selectedHeader = RecipeSortOptions.recipeName;
        sortDirection = SortDirections.asc;
        break;
      case RecipeModalSortOptions.RecipeZA:
        selectedHeader = RecipeSortOptions.recipeName;
        sortDirection = SortDirections.desc;
        break;
      case RecipeModalSortOptions.GroupAZ:
        selectedHeader = RecipeSortOptions.group;
        sortDirection = SortDirections.asc;
        break;
      case RecipeModalSortOptions.GroupZA:
        selectedHeader = RecipeSortOptions.group;
        sortDirection = SortDirections.desc;
        break;
      case RecipeModalSortOptions.Serving09:
        selectedHeader = RecipeSortOptions.serving;
        sortDirection = SortDirections.asc;
        break;
      case RecipeModalSortOptions.Serving90:
        selectedHeader = RecipeSortOptions.serving;
        sortDirection = SortDirections.desc;
        break;
      case RecipeModalSortOptions.CostServ09:
        selectedHeader = RecipeSortOptions.costServing;
        sortDirection = SortDirections.asc;
        break;
      case RecipeModalSortOptions.CostServ90:
        selectedHeader = RecipeSortOptions.costServing;
        sortDirection = SortDirections.desc;
        break;
    }

    this.store.dispatch(
      RECIPE_ACTIONS.updateRecipesSort({
        selectedHeader,
        sortDirection,
      }),
    );
  }

  selectMulitple(items: string[]) {
    this.store.dispatch(RECIPE_ACTIONS.selectMultiple({ recipeIds: items }));
  }

  deSelectMultiple(items: string[]) {
    this.store.dispatch(RECIPE_ACTIONS.deselectMultiple({ recipeIds: items }));
  }

  updateGroupFilter = (groups: string[]) => {
    this.store.dispatch(RECIPE_ACTIONS.updateGroupFilter({ groups }));
  };

  updateSearchFilter = (searchKey: string) => {
    this.store.dispatch(RECIPE_ACTIONS.updateSearch({ searchKey }));
  };

  updateFilterArchive = (flag: boolean) => {
    this.store.dispatch(RECIPE_ACTIONS.showHideArchive({ flag }));
  };
}
